/**
 * UploadToMetaModal — modal który user otwiera klikając "Wgraj na Meta" przy zatwierdzonej kreacji.
 *
 * 3 ścieżki (dynamicznie pokazane na bazie /upload-context):
 *   1. duplicate_winner — duplikat best-performing adsetu (jeśli są conversions w 7d)
 *   2. duplicate_specific — user wybiera adset z listy (top 5 po spend)
 *   3. add_to_existing — bezpośrednie wgranie do istniejącego adsetu (bez duplikatu)
 *
 * Plus: status (PAUSED/ACTIVE).
 * UGC i create_new są disabled (TBD w v2).
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Rocket, AlertTriangle, Trophy, Layers, Plus, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUploadContext,
  useUploadTaskToMeta,
  useCampaignsAndAudiences,
  type UploadContextResponse,
  type UploadToMetaInput,
} from "@/hooks/useCreativePipeline";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
  sessionId: string | null;
  agencyClientId: string | null;
}

type Mode = UploadToMetaInput["mode"];

export function UploadToMetaModal({
  open,
  onClose,
  taskId,
  taskTitle,
  sessionId,
  agencyClientId,
}: Props) {
  const { data: ctx, isLoading } = useUploadContext(sessionId);
  const uploadMut = useUploadTaskToMeta(agencyClientId);
  const { toast } = useToast();

  const [mode, setMode] = useState<Mode>("duplicate_winner");
  const [targetAdsetId, setTargetAdsetId] = useState<string | null>(null);
  const [status, setStatus] = useState<"PAUSED" | "ACTIVE">("PAUSED");
  // create_new mode state
  const [newCampaignId, setNewCampaignId] = useState<string>("");
  const [newAudienceId, setNewAudienceId] = useState<string>("");
  const [newDailyBudget, setNewDailyBudget] = useState<number>(50);
  const [newLandingUrl, setNewLandingUrl] = useState<string>("");
  const { data: campaignsAudiences, isLoading: loadingCA } = useCampaignsAndAudiences(
    mode === "create_new" ? sessionId : null
  );

  // Auto-pre-select winner jako default targetAdsetId gdy modal się otworzy
  if (open && ctx && !targetAdsetId && ctx.topAdsets.length > 0) {
    setTargetAdsetId(ctx.topAdsets[0].adsetId);
  }

  const canSubmit = (() => {
    if (!ctx) return false;
    if (!ctx.hasMetaAccount || !ctx.hasMetaPage) return false;
    if (mode === "duplicate_winner") return ctx.hasWinner;
    if (mode === "duplicate_specific" || mode === "add_to_existing") return !!targetAdsetId;
    if (mode === "create_new")
      return !!newCampaignId && newDailyBudget >= 5 && !!newLandingUrl.trim();
    return false;
  })();

  const handleSubmit = async () => {
    const options: UploadToMetaInput = {
      mode,
      status,
      ...(mode === "duplicate_specific" || mode === "add_to_existing"
        ? { targetAdsetId: targetAdsetId ?? undefined }
        : {}),
      ...(mode === "create_new"
        ? {
            newAdSetParams: {
              campaignId: newCampaignId,
              audienceId: newAudienceId || null,
              dailyBudgetPLN: newDailyBudget,
              landingPageUrl: newLandingUrl.trim(),
              geoCountry: "PL",
            },
          }
        : {}),
    };
    try {
      const result = await uploadMut.mutateAsync({ taskId, options });
      if (!result.ok) {
        toast({
          title: "Upload nie udał się",
          description: result.error ?? "Nieznany błąd",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Wgrane do Meta!",
        description:
          status === "PAUSED"
            ? `Reklama utworzona jako PAUSED w adset ${result.metaAdsetId?.slice(-8) ?? ""}. Ręcznie aktywuj w Meta Ads Manager.`
            : `Reklama LIVE w adset ${result.metaAdsetId?.slice(-8) ?? ""}.`,
      });
      onClose();
    } catch (err) {
      toast({
        title: "Błąd uploadu",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5" /> Wgraj na Meta — {taskTitle}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !ctx ? (
          <p className="text-sm text-red-400">Nie udało się załadować kontekstu sesji.</p>
        ) : (
          <ContextBody
            ctx={ctx}
            mode={mode}
            setMode={setMode}
            targetAdsetId={targetAdsetId}
            setTargetAdsetId={setTargetAdsetId}
            status={status}
            setStatus={setStatus}
            campaignsAudiences={campaignsAudiences}
            loadingCA={loadingCA}
            newCampaignId={newCampaignId}
            setNewCampaignId={setNewCampaignId}
            newAudienceId={newAudienceId}
            setNewAudienceId={setNewAudienceId}
            newDailyBudget={newDailyBudget}
            setNewDailyBudget={setNewDailyBudget}
            newLandingUrl={newLandingUrl}
            setNewLandingUrl={setNewLandingUrl}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || uploadMut.isPending}
            className="gap-2"
          >
            {uploadMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="h-4 w-4" />
            )}
            {uploadMut.isPending ? "Wgrywanie…" : `Wgraj jako ${status}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContextBody({
  ctx,
  mode,
  setMode,
  targetAdsetId,
  setTargetAdsetId,
  status,
  setStatus,
  campaignsAudiences,
  loadingCA,
  newCampaignId,
  setNewCampaignId,
  newAudienceId,
  setNewAudienceId,
  newDailyBudget,
  setNewDailyBudget,
  newLandingUrl,
  setNewLandingUrl,
}: {
  ctx: UploadContextResponse;
  mode: Mode;
  setMode: (m: Mode) => void;
  targetAdsetId: string | null;
  setTargetAdsetId: (id: string | null) => void;
  status: "PAUSED" | "ACTIVE";
  setStatus: (s: "PAUSED" | "ACTIVE") => void;
  campaignsAudiences:
    | {
        campaigns: Array<{ id: string; name: string; status: string; objective: string }>;
        audiences: Array<{ id: string; name: string; subtype: string }>;
        error?: string;
      }
    | undefined;
  loadingCA: boolean;
  newCampaignId: string;
  setNewCampaignId: (v: string) => void;
  newAudienceId: string;
  setNewAudienceId: (v: string) => void;
  newDailyBudget: number;
  setNewDailyBudget: (v: number) => void;
  newLandingUrl: string;
  setNewLandingUrl: (v: string) => void;
}) {
  const blockers: string[] = [];
  if (!ctx.hasMetaAccount)
    blockers.push("Sesja nie ma podpiętego konta Meta. Wróć do wizarda i uzupełnij.");
  if (!ctx.hasMetaPage)
    blockers.push(
      "Klient nie ma podpiętej strony Facebook (potrzebna dla creative). Uzupełnij w panelu klienta."
    );

  return (
    <div className="space-y-4 text-sm">
      {blockers.length > 0 && (
        <div className="border border-red-500/40 bg-red-500/10 rounded p-3 space-y-1">
          {blockers.map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-red-400">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="text-xs">{b}</span>
            </div>
          ))}
        </div>
      )}

      {ctx.warning && blockers.length === 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded p-3 text-xs text-amber-300 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          {ctx.warning}
        </div>
      )}

      <div>
        <label className="text-xs text-muted-foreground">Tryb wgrania (metric: {ctx.metric})</label>
        <div className="space-y-2 mt-1">
          <ModeOption
            id="duplicate_winner"
            current={mode}
            onSelect={setMode}
            disabled={!ctx.hasWinner}
            icon={<Trophy className="h-4 w-4 text-emerald-400" />}
            title="Duplikuj winnera + nowa kreacja"
            description={
              ctx.hasWinner
                ? `Theriot orthodox — kopia najlepszego adsetu (zachowany targeting), nowa kreacja. Top: spend ${ctx.topAdsets[0]?.spend ?? "?"}, ${ctx.metric}: ${ctx.topAdsets[0]?.rankValue ?? "?"}`
                : "Niedostępne — brak adsetów z konwersjami w ostatnich 7 dniach"
            }
          />
          <ModeOption
            id="duplicate_specific"
            current={mode}
            onSelect={setMode}
            disabled={ctx.topAdsets.length === 0}
            icon={<Layers className="h-4 w-4 text-blue-400" />}
            title="Duplikuj wybrany adset"
            description="Wybierz konkretny adset z listy. Czyste statystyki — nowy adset, nowa kreacja, ten sam targeting."
          />
          <ModeOption
            id="add_to_existing"
            current={mode}
            onSelect={setMode}
            disabled={ctx.topAdsets.length === 0}
            icon={<Plus className="h-4 w-4 text-cyan-400" />}
            title="Dodaj do istniejącego adsetu"
            description="Bez duplikatu — kreacja ląduje jako kolejna reklama w tym samym adsecie. Szybsze, ale stats się mieszają."
          />
          <ModeOption
            id="create_new"
            current={mode}
            onSelect={setMode}
            disabled={false}
            icon={<Sparkles className="h-4 w-4 text-purple-400" />}
            title="Stwórz nowy adset (audience picker)"
            description="Pełna kontrola — wybierz kampanię, audience (saved/lookalike/custom), budżet, landing URL. Tworzy świeży adset i ad pod nim."
          />
        </div>
      </div>

      {mode === "create_new" && (
        <div className="space-y-3 border-l-2 border-purple-500/40 pl-3">
          <div className="text-xs uppercase tracking-wider text-purple-300">
            Konfiguracja nowego adsetu
          </div>
          {loadingCA ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Ładowanie kampanii i audiences z Meta…
            </div>
          ) : campaignsAudiences?.error ? (
            <div className="text-xs text-red-400">Błąd: {campaignsAudiences.error}</div>
          ) : (
            <>
              <div>
                <label className="text-xs text-muted-foreground">
                  Kampania (parent dla nowego adsetu)
                </label>
                <Select value={newCampaignId} onValueChange={setNewCampaignId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Wybierz kampanię…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(campaignsAudiences?.campaigns ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{" "}
                        <span className="text-muted-foreground">
                          — {c.objective} ({c.status})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(campaignsAudiences?.campaigns ?? []).length === 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    Brak kampanii na koncie. Stwórz ją w Meta Ads Manager najpierw.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  Audience (custom / saved / lookalike) — opcjonalne
                </label>
                <Select value={newAudienceId} onValueChange={setNewAudienceId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Broad — bez custom audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Broad — bez custom audience (PL only, 18-65)</SelectItem>
                    {(campaignsAudiences?.audiences ?? []).map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} <span className="text-muted-foreground">— {a.subtype}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Daily budget (PLN)</label>
                  <Input
                    type="number"
                    min={5}
                    value={newDailyBudget}
                    onChange={(e) => setNewDailyBudget(Number(e.target.value) || 0)}
                    className="mt-1"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Min 5 zł — Meta odrzuci niższe.
                  </p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Landing page URL</label>
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={newLandingUrl}
                    onChange={(e) => setNewLandingUrl(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {(mode === "duplicate_specific" || mode === "add_to_existing") &&
        ctx.topAdsets.length > 0 && (
          <div>
            <label className="text-xs text-muted-foreground">Wybierz adset</label>
            <div className="space-y-1 mt-1 max-h-48 overflow-y-auto">
              {ctx.topAdsets.map((a) => {
                const selected = targetAdsetId === a.adsetId;
                return (
                  <button
                    key={a.adsetId}
                    type="button"
                    onClick={() => setTargetAdsetId(a.adsetId)}
                    className={`w-full text-left border rounded p-2 transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-mono text-xs truncate">{a.adsetId}</div>
                      <div className="flex gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px]">
                          spend: {a.spend}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          conv: {a.conversions}
                        </Badge>
                        {a.rankValue !== null && (
                          <Badge variant="secondary" className="text-[10px]">
                            {ctx.metric}: {a.rankValue}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      <div>
        <label className="text-xs text-muted-foreground">Status nowej reklamy</label>
        <div className="flex gap-2 mt-1">
          <button
            type="button"
            onClick={() => setStatus("PAUSED")}
            className={`flex-1 border rounded p-2 text-xs ${status === "PAUSED" ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
          >
            <div className="font-medium">PAUSED (zalecane)</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Reklama utworzona, ale wymaga ręcznej aktywacji w Meta Ads Manager
            </div>
          </button>
          <button
            type="button"
            onClick={() => setStatus("ACTIVE")}
            className={`flex-1 border rounded p-2 text-xs ${status === "ACTIVE" ? "border-emerald-500 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}
          >
            <div className="font-medium">ACTIVE (od razu live)</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Reklama startuje natychmiast z budżetem winnera
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeOption({
  id,
  current,
  onSelect,
  disabled,
  icon,
  title,
  description,
}: {
  id: Mode;
  current: Mode;
  onSelect: (m: Mode) => void;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  const selected = current === id;
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(id)}
      disabled={disabled}
      className={`w-full text-left border rounded p-3 transition ${selected ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/30"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <div className="flex items-start gap-2">
        {icon}
        <div className="flex-1">
          <div className="font-medium text-sm">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
        </div>
      </div>
    </button>
  );
}
